
import { useState, useCallback, useEffect, useRef } from "react";
import { User } from "@/types";
import logger from "@/utils/logger";
import { setFacebookUserId } from "@/lib/facebook";
import { setBillingUser } from "@/services/billing/revenuecat.service";

export interface AuthState {
  user: User | null;
  isLoading: boolean;
}

export const useAuthState = () => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const loadingTimeoutRef = useRef<number | null>(null);
  const [authInitialized, setAuthInitialized] = useState(false);
  const prevUserRef = useRef<User | null>(null);
  const prevLoadingRef = useRef<boolean>(true);

  // Set a maximum loading time to prevent infinite loading
  useEffect(() => {
    // Clear any previous timeout
    if (loadingTimeoutRef.current) {
      window.clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }

    // Set a new timeout if we're loading
    if (isLoading) {
      logger.log("[LOADING_SAFETY] Setting loading timeout safety");
      loadingTimeoutRef.current = window.setTimeout(() => {
        logger.log("[LOADING_SAFETY] Loading safety timeout triggered - forcing loading to false");
        setIsLoading(false);
      }, 5000); // 5 seconds maximum loading time
    }

    return () => {
      if (loadingTimeoutRef.current) {
        window.clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
    };
  }, [isLoading]);

  // Create stable setter functions with checks to avoid unnecessary updates
  const setUserStable = useCallback((newUser: User | null) => {
    // Only update if the user has actually changed
    const newUserStr = JSON.stringify(newUser);
    const prevUserStr = JSON.stringify(prevUserRef.current);
    
    if (prevUserStr !== newUserStr) {
      logger.log("[AUTH_STATE] Setting user state:", newUser ? `User exists: ${newUser.email}` : "No user");
      prevUserRef.current = newUser;
      setUser(newUser);
      void setFacebookUserId(newUser?.id ?? null);
      void setBillingUser(newUser?.id ?? null);
      setAuthInitialized(true);
    } else if (!authInitialized) {
      // Make sure we set initialized even if user hasn't changed
      setAuthInitialized(true);
    }
  }, [authInitialized]);

  const setIsLoadingStable = useCallback((loading: boolean) => {
    // Only update if loading state has actually changed
    if (prevLoadingRef.current !== loading) {
      logger.log("[AUTH_STATE] Setting loading state:", loading);
      prevLoadingRef.current = loading;
      setIsLoading(loading);
    }
  }, []);

  return {
    user,
    setUser: setUserStable,
    isLoading,
    setIsLoading: setIsLoadingStable,
    authInitialized,
  };
};
